import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { PageContainer, PageHeading } from "../ui";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
} from "../../utils/themeUtils";

interface FeatureBullet {
  icon?: React.ReactNode;
  title: string;
  description?: string;
}

interface ComingSoonPageProps {
  /** The feature name shown as the page title (e.g. "Unified Inbox"). */
  title: string;
  /** One-sentence summary under the title. */
  tagline?: string;
  /** Bulleted list of capabilities the feature will provide. */
  bullets?: FeatureBullet[];
  /** Optional footer note (e.g. "Roadmap target: Q3 2026"). */
  footnote?: string;
}

/**
 * Generic "Coming Soon" placeholder used for features whose backend
 * is built (or partially built) but the UI is intentionally deferred.
 *
 * Reserves the route, signals to staff that the feature is on the
 * roadmap, and previews the capabilities so they can plan around it.
 * Avoids the lazy "Coming Soon" text dump that leaves staff guessing
 * what the feature will actually do.
 */
const ComingSoonPage: React.FC<ComingSoonPageProps> = ({
  title,
  tagline,
  bullets = [],
  footnote,
}) => {
  const { isDarkMode } = useTheme();

  return (
    <PageContainer>
      <PageHeading
        title={title}
        subtitle={tagline}
        actions={
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              isDarkMode ? "bg-blue-900 text-blue-200" : "bg-blue-100 text-blue-700"
            }`}
          >
            COMING SOON
          </span>
        }
      />

      {/* Bullets — what's coming */}
      {bullets.length > 0 && (
        <div className={`rounded-lg p-6 ${getCardBackground(isDarkMode)} border ${getLightBorder(isDarkMode)}`}>
          <h2 className={`text-lg font-semibold mb-4 ${getPrimaryText(isDarkMode)}`}>
            What you'll be able to do
          </h2>
          <ul className="space-y-4">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                {b.icon && (
                  <div className={`flex-shrink-0 mt-0.5 ${getPrimaryText(isDarkMode)}`}>
                    {b.icon}
                  </div>
                )}
                <div>
                  <p className={`font-medium ${getPrimaryText(isDarkMode)}`}>{b.title}</p>
                  {b.description && (
                    <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>{b.description}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footnote */}
      {footnote && (
        <p className={`mt-6 text-sm text-center ${getMutedText(isDarkMode)}`}>{footnote}</p>
      )}
    </PageContainer>
  );
};

export default ComingSoonPage;
